// apps/web/src/components/Account/PublicProfileWeb.jsx
import React, { useEffect, useState } from 'react';
import useGetPublicProfile from '@mysimpledb/shared/hooks/useGetPublicProfile';
import useUpdatePublicProfile from '@mysimpledb/shared/hooks/useUpdatePublicProfile';
import CropUploader from '@mysimpledb/shared/components/UI/CropUploader';
import axios from '@mysimpledb/shared/config/axios';
import { buildAvatarUrl } from '@mysimpledb/shared/utils/mediaUrls';
import PhoneNumberField, {
  normalizePhoneForSave,
} from '@mysimpledb/shared/components/PhoneNumberField/PhoneNumberField';
import './PublicProfile.css';

const EMPTY_PROFILE = {
  isVisible: false,
  profileType: 'personal',
  displayName: '',
  headline: '',
  location: '',
  bio: '',
  useAccountProfilePicture: true,
  profilePicturePath: '',
  businessName: '',
  businessLogoPath: '',
  businessDescription: '',
  serviceArea: '',
  websiteUrl: '',
  businessEmail: '',
  showBusinessEmail: false,
  businessPhoneCountry: '',
  businessPhoneE164: '',
  businessPhoneDisplay: '',
  businessPhoneExtension: '',
  showBusinessPhone: false,
  acceptingClients: false,
  skills: [],
  links: [],
  openToCollaboration: false,
  openToQuestions: false,
  openToServiceInquiries: false,
  allowContactRequests: false,
  defaultAttribution: 'personal',
};

function normalizeProfileData(data = {}) {
  return {
    ...EMPTY_PROFILE,
    ...data,
    displayName: data.displayName || '',
    headline: data.headline || '',
    location: data.location || '',
    bio: data.bio || '',
    profilePicturePath: data.profilePicturePath || '',
    accountProfilePicture: data.accountProfilePicture || '',
    businessName: data.businessName || '',
    businessLogoPath: data.businessLogoPath || '',
    businessDescription: data.businessDescription || '',
    serviceArea: data.serviceArea || '',
    websiteUrl: data.websiteUrl || '',
    businessEmail: data.businessEmail || '',
    businessPhoneCountry: data.businessPhoneCountry || '',
    businessPhoneE164: data.businessPhoneE164 || '',
    businessPhoneDisplay: data.businessPhoneDisplay || '',
    businessPhoneExtension: data.businessPhoneExtension || '',
    skills: Array.isArray(data.skills) ? data.skills : [],
    links: Array.isArray(data.links) ? data.links : [],
  };
}

const PublicProfileWeb = () => {
  const token = localStorage.getItem('token');
  const { data, loading, error, refetch } = useGetPublicProfile(token);
  const { updateProfile, updating } = useUpdatePublicProfile(token);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [formErrors, setFormErrors] = useState({});
  const [skillInput, setSkillInput] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [profileImageDraft, setProfileImageDraft] = useState(null);
  const [profileImageDraftUrl, setProfileImageDraftUrl] = useState('');
  const [businessLogoDraft, setBusinessLogoDraft] = useState(null);
  const [businessLogoDraftUrl, setBusinessLogoDraftUrl] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    if (!data) return;

    setProfile(normalizeProfileData(data));
  }, [data]);

  useEffect(() => {
    return () => {
      if (profileImageDraftUrl) URL.revokeObjectURL(profileImageDraftUrl);
    };
  }, [profileImageDraftUrl]);

  useEffect(() => {
    return () => {
      if (businessLogoDraftUrl) URL.revokeObjectURL(businessLogoDraftUrl);
    };
  }, [businessLogoDraftUrl]);

  const isBusiness = profile.profileType !== 'personal';
  const isCombined = profile.profileType === 'personal_business';

  const setField = (field, value) => {
    setProfile((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: null }));
    setSavedMessage('');
  };

  const setImageDraft = (type, draft) => {
    const isLogo = type === 'logo';
    const currentUrl = isLogo
      ? businessLogoDraftUrl
      : profileImageDraftUrl;

    if (currentUrl) URL.revokeObjectURL(currentUrl);

    const nextUrl = draft?.blob ? URL.createObjectURL(draft.blob) : '';

    if (isLogo) {
      setBusinessLogoDraft(draft);
      setBusinessLogoDraftUrl(nextUrl);
    } else {
      setProfileImageDraft(draft);
      setProfileImageDraftUrl(nextUrl);
      setField('useAccountProfilePicture', false);
    }

    setSavedMessage('');
  };

  const uploadImageDraft = async (endpoint, draft) => {
    const formData = new FormData();
    formData.append('image', draft.blob, draft.name || 'image.jpg');

    const response = await axios.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const storageKey = response?.data?.storageKey;

    if (!storageKey) {
      throw new Error('Image upload did not return a storage key.');
    }

    return storageKey;
  };

  const removeSavedImage = async (type) => {
    const isLogo = type === 'logo';
    const draft = isLogo ? businessLogoDraft : profileImageDraft;

    if (draft) {
      setImageDraft(type, null);
      return;
    }

    const hasSavedImage = isLogo
      ? profile.businessLogoPath
      : profile.profilePicturePath;

    if (!hasSavedImage) return;

    const label = isLogo ? 'business logo' : 'public profile image';
    if (!window.confirm(`Remove this ${label}?`)) return;

    try {
      setUploadingImages(true);
      await axios.delete(
        isLogo
          ? '/users/me/public-profile/logo'
          : '/users/me/public-profile/image'
      );
      setProfile((current) => ({
        ...current,
        [isLogo ? 'businessLogoPath' : 'profilePicturePath']: '',
      }));
      setSavedMessage(
        isLogo ? 'Business logo removed.' : 'Public profile image removed.'
      );
    } catch (err) {
      setFormErrors((current) => ({
        ...current,
        form: err?.response?.data?.error || `Unable to remove ${label}.`,
      }));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleProfileTypeChange = (profileType) => {
    setProfile((current) => ({
      ...current,
      profileType,
      defaultAttribution:
        profileType === 'business' ? 'business' : 'personal',
    }));
    setFormErrors({});
    setSavedMessage('');
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (!skill) return;

    if (skill.length > 50) {
      setFormErrors((current) => ({
        ...current,
        skills: 'Each skill must be 50 characters or fewer.',
      }));
      return;
    }

    if (profile.skills.length >= 30) {
      setFormErrors((current) => ({
        ...current,
        skills: 'You may add up to 30 skills.',
      }));
      return;
    }

    if (
      profile.skills.some(
        (existing) => existing.toLowerCase() === skill.toLowerCase()
      )
    ) {
      setSkillInput('');
      return;
    }

    setField('skills', [...profile.skills, skill]);
    setSkillInput('');
  };

  const removeSkill = (skill) => {
    setField(
      'skills',
      profile.skills.filter((existing) => existing !== skill)
    );
  };

  const addLink = () => {
    if (profile.links.length >= 10) return;
    setField('links', [...profile.links, { label: '', url: '' }]);
  };

  const updateLink = (index, field, value) => {
    setField(
      'links',
      profile.links.map((link, linkIndex) =>
        linkIndex === index ? { ...link, [field]: value } : link
      )
    );
  };

  const removeLink = (index) => {
    setField(
      'links',
      profile.links.filter((_, linkIndex) => linkIndex !== index)
    );
  };

  const validate = () => {
    const errors = {};

    if (profile.isVisible && !profile.displayName.trim()) {
      errors.displayName = 'Display name is required when the profile is shown.';
    }

    if (profile.isVisible && isBusiness && !profile.businessName.trim()) {
      errors.businessName = 'Business name is required for a business profile.';
    }

    if (profile.showBusinessEmail && !profile.businessEmail.trim()) {
      errors.businessEmail = 'Enter an email before making it visible.';
    }

    const phoneDisplay = profile.businessPhoneDisplay.trim();
    let phoneE164 = null;

    if (phoneDisplay) {
      try {
        phoneE164 = normalizePhoneForSave(
          phoneDisplay,
          profile.businessPhoneCountry || null
        );
      } catch {
        phoneE164 = null;
      }

      if (!phoneE164) {
        errors.businessPhone = 'Enter a valid international phone number.';
      }
    }

    if (profile.showBusinessPhone && !phoneE164) {
      errors.businessPhone = phoneDisplay
        ? 'Enter a valid international phone number.'
        : 'Enter a phone number before making it visible.';
    }

    if (
      profile.businessPhoneExtension &&
      !/^\d{1,10}$/.test(profile.businessPhoneExtension)
    ) {
      errors.businessPhoneExtension =
        'Extension must contain 1 to 10 digits.';
    }

    profile.links.forEach((link, index) => {
      if (!link.url.trim()) return;

      try {
        const parsed = new URL(link.url);
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
      } catch {
        errors[`link-${index}`] = 'Enter a complete http or https URL.';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setUploadingImages(true);
      let profilePicturePath = profile.profilePicturePath || null;
      let businessLogoPath = profile.businessLogoPath || null;

      if (!profile.useAccountProfilePicture && profileImageDraft?.blob) {
        profilePicturePath = await uploadImageDraft(
          '/users/me/public-profile/image',
          profileImageDraft
        );
      }

      if (businessLogoDraft?.blob) {
        businessLogoPath = await uploadImageDraft(
          '/users/me/public-profile/logo',
          businessLogoDraft
        );
      }

      const businessPhoneE164 = profile.businessPhoneDisplay.trim()
        ? normalizePhoneForSave(
            profile.businessPhoneDisplay,
            profile.businessPhoneCountry || null
          )
        : null;

      const result = await updateProfile({
        ...profile,
        profilePicturePath,
        businessLogoPath,
        businessPhoneCountry: businessPhoneE164
          ? profile.businessPhoneCountry
          : null,
        businessPhoneE164,
        businessPhoneDisplay: businessPhoneE164
          ? profile.businessPhoneDisplay.trim()
          : null,
        businessPhoneExtension: businessPhoneE164
          ? profile.businessPhoneExtension || null
          : null,
      });
      if (result?.profile) {
        setProfile(normalizeProfileData(result.profile));
      }
      setProfileImageDraft(null);
      setProfileImageDraftUrl('');
      setBusinessLogoDraft(null);
      setBusinessLogoDraftUrl('');
      setSavedMessage(
        profile.isVisible
          ? 'Profile saved. Your public profile is visible.'
          : 'Profile saved. Your public profile is hidden.'
      );
      await refetch();
    } catch (err) {
      setFormErrors((current) => ({
        ...current,
        form:
          err?.response?.data?.error ||
          err.message ||
          'Unable to save the public profile.',
      }));
    } finally {
      setUploadingImages(false);
    }
  };

  if (loading) {
    return <div className="public-profile-loading">Loading public profile…</div>;
  }

  if (error) {
    return (
      <div className="public-profile-error-panel">
        Unable to load the public profile.
      </div>
    );
  }

  const publicProfileImageUrl = profileImageDraftUrl ||
    (profile.profilePicturePath
      ? buildAvatarUrl(profile.profilePicturePath)
      : '');
  const accountProfileImageUrl = profile.accountProfilePicture
    ? buildAvatarUrl(profile.accountProfilePicture)
    : '';
  const businessLogoUrl = businessLogoDraftUrl ||
    (profile.businessLogoPath ? buildAvatarUrl(profile.businessLogoPath) : '');

  return (
    <form className="public-profile-form" onSubmit={handleSave}>
      <div className="public-profile-heading">
        <div>
          <h1>Public Profile</h1>
          <p>
            Create a profile that signed-in MySimpleDB users can view when
            they click your name or picture on a shared entry.
          </p>
        </div>
        <span
          className={`public-profile-status ${
            profile.isVisible ? 'is-visible' : 'is-hidden'
          }`}
        >
          {profile.isVisible ? 'Visible' : 'Hidden'}
        </span>
      </div>

      <section className="public-profile-card public-profile-visibility-card">
        <label className="public-profile-check-row">
          <input
            type="checkbox"
            checked={profile.isVisible}
            onChange={(event) => setField('isVisible', event.target.checked)}
          />
          <span>
            <strong>Show my public profile</strong>
            <small>
              When disabled, your profile information remains saved but other
              users cannot open the profile.
            </small>
          </span>
        </label>
      </section>

      <section className="public-profile-section">
        <h2>Profile Type</h2>
        <div className="public-profile-choice-grid">
          {[
            ['personal', 'Personal', 'Share information about yourself.'],
            ['business', 'Business', 'Promote a business or service.'],
            [
              'personal_business',
              'Personal & Business',
              'Show both your identity and business.',
            ],
          ].map(([value, title, description]) => (
            <label
              key={value}
              className={`public-profile-choice ${
                profile.profileType === value ? 'selected' : ''
              }`}
            >
              <input
                type="radio"
                name="profileType"
                value={value}
                checked={profile.profileType === value}
                onChange={() => handleProfileTypeChange(value)}
              />
              <span>
                <strong>{title}</strong>
                <small>{description}</small>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="public-profile-section">
        <h2>About You</h2>
        <div className="public-profile-grid">
          <div className="public-profile-field">
            <label htmlFor="public-display-name">Display Name</label>
            <input
              id="public-display-name"
              type="text"
              maxLength={80}
              value={profile.displayName}
              onChange={(event) => setField('displayName', event.target.value)}
            />
            {formErrors.displayName && (
              <span className="public-profile-field-error">
                {formErrors.displayName}
              </span>
            )}
          </div>

          <div className="public-profile-field">
            <label htmlFor="public-headline">Headline</label>
            <input
              id="public-headline"
              type="text"
              maxLength={160}
              placeholder="Developer, musician, consultant…"
              value={profile.headline}
              onChange={(event) => setField('headline', event.target.value)}
            />
          </div>

          <div className="public-profile-field public-profile-field-wide">
            <label htmlFor="public-location">General Location</label>
            <input
              id="public-location"
              type="text"
              maxLength={120}
              placeholder="Long Beach, California"
              value={profile.location}
              onChange={(event) => setField('location', event.target.value)}
            />
            <small>Do not enter a street address.</small>
          </div>

          <div className="public-profile-field public-profile-field-wide">
            <label htmlFor="public-bio">About</label>
            <textarea
              id="public-bio"
              maxLength={1000}
              rows={6}
              value={profile.bio}
              onChange={(event) => setField('bio', event.target.value)}
            />
            <span className="public-profile-character-count">
              {(profile.bio || '').length}/1000
            </span>
          </div>
        </div>

        <label className="public-profile-check-row public-profile-picture-option">
          <input
            type="checkbox"
            checked={profile.useAccountProfilePicture}
            onChange={(event) => {
              const useAccountPicture = event.target.checked;
              if (useAccountPicture && profileImageDraft) {
                setImageDraft('profile', null);
              }
              setField('useAccountProfilePicture', useAccountPicture);
            }}
          />
          <span>
            <strong>Use my account profile picture</strong>
            <small>
              Use the same picture shown on your private account profile.
            </small>
          </span>
        </label>

        <div className="public-profile-image-editor">
          <div className="public-profile-image-preview-wrap">
            {profile.useAccountProfilePicture && accountProfileImageUrl ? (
              <img
                className="public-profile-image-preview"
                src={accountProfileImageUrl}
                alt="Account profile preview"
              />
            ) : publicProfileImageUrl ? (
              <img
                className="public-profile-image-preview"
                src={publicProfileImageUrl}
                alt="Public profile preview"
              />
            ) : (
              <div className="public-profile-image-placeholder">
                No public image
              </div>
            )}
          </div>

          {!profile.useAccountProfilePicture && (
            <div className="public-profile-image-controls">
              <strong>Separate public profile image</strong>
              <small>JPG or PNG, up to 5 MB. The saved image is square.</small>
              <CropUploader
                aspect={1}
                buttonLabel="Use this image"
                onCropComplete={(draft) => setImageDraft('profile', draft)}
              />
              {(profileImageDraft || profile.profilePicturePath) && (
                <button
                  type="button"
                  className="public-profile-remove-image-button"
                  disabled={uploadingImages}
                  onClick={() => removeSavedImage('profile')}
                >
                  {profileImageDraft
                    ? 'Cancel new image'
                    : 'Remove public image'}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {isBusiness && (
        <section className="public-profile-section">
          <h2>Business</h2>
          <div className="public-profile-grid">
            <div className="public-profile-field">
              <label htmlFor="business-name">Business Name</label>
              <input
                id="business-name"
                type="text"
                maxLength={120}
                value={profile.businessName}
                onChange={(event) =>
                  setField('businessName', event.target.value)
                }
              />
              {formErrors.businessName && (
                <span className="public-profile-field-error">
                  {formErrors.businessName}
                </span>
              )}
            </div>

            <div className="public-profile-field">
              <label htmlFor="service-area">Service Area</label>
              <input
                id="service-area"
                type="text"
                maxLength={160}
                placeholder="Southern California or remote"
                value={profile.serviceArea}
                onChange={(event) =>
                  setField('serviceArea', event.target.value)
                }
              />
            </div>

            <div className="public-profile-field public-profile-field-wide">
              <label htmlFor="business-description">Business Description</label>
              <textarea
                id="business-description"
                maxLength={1000}
                rows={5}
                value={profile.businessDescription}
                onChange={(event) =>
                  setField('businessDescription', event.target.value)
                }
              />
              <span className="public-profile-character-count">
                {(profile.businessDescription || '').length}/1000
              </span>
            </div>

            <div className="public-profile-field public-profile-field-wide">
              <label htmlFor="business-website">Website</label>
              <input
                id="business-website"
                type="url"
                placeholder="https://example.com"
                value={profile.websiteUrl}
                onChange={(event) => setField('websiteUrl', event.target.value)}
              />
            </div>

            <div className="public-profile-field">
              <label htmlFor="business-email">Business Email</label>
              <input
                id="business-email"
                type="email"
                value={profile.businessEmail}
                onChange={(event) =>
                  setField('businessEmail', event.target.value)
                }
              />
              {formErrors.businessEmail && (
                <span className="public-profile-field-error">
                  {formErrors.businessEmail}
                </span>
              )}
              <label className="public-profile-inline-check">
                <input
                  type="checkbox"
                  checked={profile.showBusinessEmail}
                  onChange={(event) =>
                    setField('showBusinessEmail', event.target.checked)
                  }
                />
                Show on my profile
              </label>
            </div>

            <div className="public-profile-field public-profile-field-wide public-profile-business-phone">
              <PhoneNumberField
                label="Business Contact Phone"
                countryValue={profile.businessPhoneCountry}
                onCountryChange={(value) => {
                  setProfile((current) => ({
                    ...current,
                    businessPhoneCountry: value,
                    businessPhoneE164: '',
                  }));
                  setFormErrors((current) => ({
                    ...current,
                    businessPhone: null,
                  }));
                  setSavedMessage('');
                }}
                phoneValue={profile.businessPhoneDisplay}
                onPhoneChange={(value) => {
                  const hasPhone = String(value || '').trim().length > 0;

                  setProfile((current) => ({
                    ...current,
                    businessPhoneDisplay: value,
                    businessPhoneE164: '',
                    businessPhoneExtension: hasPhone
                      ? current.businessPhoneExtension
                      : '',
                    showBusinessPhone: hasPhone
                      ? current.showBusinessPhone
                      : false,
                  }));
                  setFormErrors((current) => ({
                    ...current,
                    businessPhone: null,
                    businessPhoneExtension: hasPhone
                      ? current.businessPhoneExtension
                      : null,
                  }));
                  setSavedMessage('');
                }}
              />
              {formErrors.businessPhone && (
                <span className="public-profile-field-error">
                  {formErrors.businessPhone}
                </span>
              )}

              <div className="public-profile-phone-extension">
                <label htmlFor="business-phone-extension">
                  Extension (optional)
                </label>
                <input
                  id="business-phone-extension"
                  type="text"
                  inputMode="numeric"
                  autoComplete="tel-extension"
                  maxLength={10}
                  value={profile.businessPhoneExtension}
                  onChange={(event) =>
                    setField(
                      'businessPhoneExtension',
                      event.target.value.replace(/\D/g, '').slice(0, 10)
                    )
                  }
                />
                {formErrors.businessPhoneExtension && (
                  <span className="public-profile-field-error">
                    {formErrors.businessPhoneExtension}
                  </span>
                )}
              </div>

              <label className="public-profile-inline-check">
                <input
                  type="checkbox"
                  checked={profile.showBusinessPhone}
                  onChange={(event) =>
                    setField('showBusinessPhone', event.target.checked)
                  }
                />
                Show on my profile
              </label>
            </div>
          </div>

          <div className="public-profile-logo-editor">
            <div className="public-profile-logo-preview-wrap">
              {businessLogoUrl ? (
                <img
                  className="public-profile-logo-preview"
                  src={businessLogoUrl}
                  alt="Business logo preview"
                />
              ) : (
                <div className="public-profile-logo-empty">
                  No business logo
                </div>
              )}
            </div>

            <div className="public-profile-image-controls">
              <strong>Business Logo</strong>
              <small>
                JPG or PNG, up to 5 MB. Recommended size: 1200 × 400 px
                (3:1). For the best display, the logo should fill most of the
                image with minimal empty space around it.
              </small>
              <CropUploader
                aspect={3}
                cropperWidth={420}
                cropperHeight={180}
                showFitControls
                minZoom={0.25}
                fitLabel="Fit entire logo"
                fillLabel="Fill crop area"
                buttonLabel="Use this logo"
                onCropComplete={(draft) => setImageDraft('logo', draft)}
              />
              {(businessLogoDraft || profile.businessLogoPath) && (
                <button
                  type="button"
                  className="public-profile-remove-image-button"
                  disabled={uploadingImages}
                  onClick={() => removeSavedImage('logo')}
                >
                  {businessLogoDraft
                    ? 'Cancel new logo'
                    : 'Remove business logo'}
                </button>
              )}
            </div>
          </div>

          <label className="public-profile-check-row">
            <input
              type="checkbox"
              checked={profile.acceptingClients}
              onChange={(event) =>
                setField('acceptingClients', event.target.checked)
              }
            />
            <span>
              <strong>Accepting new clients</strong>
              <small>Show visitors that your services are currently available.</small>
            </span>
          </label>
        </section>
      )}

      <section className="public-profile-section">
        <h2>Skills & Interests</h2>
        <div className="public-profile-tag-entry">
          <input
            type="text"
            maxLength={50}
            placeholder="Enter a skill and press Enter"
            value={skillInput}
            onChange={(event) => setSkillInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addSkill();
              }
            }}
          />
          <button type="button" onClick={addSkill}>
            Add
          </button>
        </div>
        {formErrors.skills && (
          <span className="public-profile-field-error">{formErrors.skills}</span>
        )}
        <div className="public-profile-tags">
          {profile.skills.map((skill) => (
            <span key={skill} className="public-profile-tag">
              {skill}
              <button
                type="button"
                aria-label={`Remove ${skill}`}
                onClick={() => removeSkill(skill)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </section>

      <section className="public-profile-section">
        <div className="public-profile-section-heading-row">
          <div>
            <h2>Links</h2>
            <p>Add a website, portfolio, LinkedIn, YouTube or another link.</p>
          </div>
          <button
            type="button"
            className="public-profile-secondary-button"
            onClick={addLink}
            disabled={profile.links.length >= 10}
          >
            Add Link
          </button>
        </div>

        <div className="public-profile-links">
          {profile.links.map((link, index) => (
            <div key={index} className="public-profile-link-row">
              <input
                type="text"
                maxLength={40}
                aria-label={`Link ${index + 1} label`}
                placeholder="Label"
                value={link.label}
                onChange={(event) =>
                  updateLink(index, 'label', event.target.value)
                }
              />
              <input
                type="url"
                aria-label={`Link ${index + 1} URL`}
                placeholder="https://example.com"
                value={link.url}
                onChange={(event) =>
                  updateLink(index, 'url', event.target.value)
                }
              />
              <button type="button" onClick={() => removeLink(index)}>
                Remove
              </button>
              {formErrors[`link-${index}`] && (
                <span className="public-profile-field-error">
                  {formErrors[`link-${index}`]}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="public-profile-section">
        <h2>Contact Preferences</h2>
        <p className="public-profile-section-description">
          Your private account email and SMS phone number are never shown.
        </p>

        <label className="public-profile-check-row">
          <input
            type="checkbox"
            checked={profile.allowContactRequests}
            onChange={(event) =>
              setField('allowContactRequests', event.target.checked)
            }
          />
          <span>
            <strong>Allow contact requests through MySimpleDB</strong>
            <small>
              This preference prepares your profile for internal communication.
            </small>
          </span>
        </label>

        <div className="public-profile-preference-grid">
          <label className="public-profile-inline-check">
            <input
              type="checkbox"
              checked={profile.openToQuestions}
              onChange={(event) =>
                setField('openToQuestions', event.target.checked)
              }
            />
            Questions about my entries
          </label>
          <label className="public-profile-inline-check">
            <input
              type="checkbox"
              checked={profile.openToCollaboration}
              onChange={(event) =>
                setField('openToCollaboration', event.target.checked)
              }
            />
            Collaboration requests
          </label>
          <label className="public-profile-inline-check">
            <input
              type="checkbox"
              checked={profile.openToServiceInquiries}
              onChange={(event) =>
                setField('openToServiceInquiries', event.target.checked)
              }
            />
            Service inquiries
          </label>
        </div>
      </section>

      {isCombined && (
        <section className="public-profile-section">
          <h2>Shared Entry Attribution</h2>
          <div className="public-profile-attribution-options">
            {[
              ['personal', profile.displayName || 'Personal name'],
              ['business', profile.businessName || 'Business name'],
              [
                'personal_business',
                `${profile.displayName || 'Personal name'} · ${
                  profile.businessName || 'Business name'
                }`,
              ],
            ].map(([value, label]) => (
              <label key={value} className="public-profile-inline-check">
                <input
                  type="radio"
                  name="defaultAttribution"
                  value={value}
                  checked={profile.defaultAttribution === value}
                  onChange={() => setField('defaultAttribution', value)}
                />
                {label}
              </label>
            ))}
          </div>
        </section>
      )}

      {formErrors.form && (
        <div className="public-profile-form-error">{formErrors.form}</div>
      )}
      {savedMessage && (
        <div className="public-profile-save-message">{savedMessage}</div>
      )}

      <div className="public-profile-actions">
        <button type="submit" disabled={updating || uploadingImages}>
          {updating || uploadingImages ? 'Saving…' : 'Save Public Profile'}
        </button>
      </div>
    </form>
  );
};

export default PublicProfileWeb;
